import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPlayersRanking } from '../api';
import PlayerCard from '../components/PlayerCard';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';

export default function Rankings() {
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPlayers, setTotalPlayers] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [minBlocks, setMinBlocks] = useState(1000);
  const pageSize = 25;

  const fetchRanking = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getPlayersRanking(page, pageSize, search, minBlocks);
      setPlayers(data.players || []);
      setTotalPages(data.total_pages || 1);
      setTotalPlayers(data.total_players || 0);
    } catch (err) {
      setError(err.message || 'Error cargando ranking');
    } finally {
      setLoading(false);
    }
  }, [page, search, minBlocks]);

  useEffect(() => { fetchRanking(); }, [fetchRanking]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="mc-title text-mc-gold text-xl">🏆 Ranking de Jugadores</h1>
        <span className="text-mc-muted text-sm">{totalPlayers} jugadores</span>
      </div>

      {/* Filters */}
      <div className="mc-border rounded-xl p-4 bg-mc-card flex flex-wrap gap-4 items-end">
        <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
          <label className="text-mc-muted text-xs block mb-1">Buscar jugador</label>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Nombre de usuario..."
            className="w-full bg-mc-darker border border-mc-border rounded-lg px-3 py-2 text-sm text-mc-text placeholder-mc-muted focus:outline-none focus:border-mc-accent"
          />
        </form>
        <div>
          <label className="text-mc-muted text-xs block mb-1">Mín. bloques</label>
          <select
            value={minBlocks}
            onChange={(e) => { setMinBlocks(Number(e.target.value)); setPage(1); }}
            className="bg-mc-darker border border-mc-border rounded-lg px-3 py-2 text-sm text-mc-text focus:outline-none focus:border-mc-accent"
          >
            <option value={1000}>1,000+</option>
            <option value={5000}>5,000+</option>
            <option value={10000}>10,000+</option>
            <option value={50000}>50,000+</option>
            <option value={100000}>100,000+</option>
          </select>
        </div>
        <button
          type="button"
          onClick={() => { setSearch(''); setSearchInput(''); setMinBlocks(1000); setPage(1); }}
          className="px-3 py-2 text-sm text-mc-muted hover:text-mc-accent transition-colors"
        >
          Limpiar
        </button>
      </div>

      {/* Results */}
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage message={error} onRetry={fetchRanking} />
      ) : players.length === 0 ? (
        <div className="text-center py-12 text-mc-muted">
          <p className="text-4xl mb-4">🔍</p>
          <p>No se encontraron jugadores</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {players.map((p) => (
            <PlayerCard
              key={p.username}
              rank={p.rank}
              username={p.username}
              blocks={p.mined_blocks}
              onClick={() => navigate(`/player/${p.username}`)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-4">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 mc-border rounded-lg bg-mc-card text-mc-text disabled:opacity-30 hover:border-mc-accent/50 transition-colors text-sm"
          >
            ← Anterior
          </button>
          <span className="text-mc-muted text-sm">
            Página {page} de {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 mc-border rounded-lg bg-mc-card text-mc-text disabled:opacity-30 hover:border-mc-accent/50 transition-colors text-sm"
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
}
